import { Aide } from '@/components/ui/aide';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Building2,
  CalendarDays,
  Check,
  ChevronRight,
  Clock,
  Copy,
  CreditCard,
  Handshake,
  IdCard,
  Key,
  Link2,
  Lock,
  LogOut,
  Mail,
  Map,
  MapPin,
  MessageCircle,
  Phone,
  RefreshCw,
  Shield,
  Eye,
  EyeOff,
  Plus,
  ShoppingCart,
  Truck,
  Unlink,
  User,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';

import { usersAPI, whatsappBotAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { ProfileSection, ProfileReadOnly as ChampLecture, ProfileLink as LigneAcces, ProfileRail, ProfileSaveBar } from '../components/profile/ProfileWorkspace';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { PageHero } from '../components/dashboard/PageHero';
import FiscalYearSetting from '../components/finance/FiscalYearSetting';
import ChoixAlertesWhatsapp from '../components/profile/ChoixAlertesWhatsapp';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { SUB_ACCOUNT_ROLES, roleLabel } from '../lib/roles';
import { messageErreur } from '../lib/erreurs';
import { formatDate, formatHeure } from '../lib/formats';
import { libelle } from '../lib/libelles';
import {
  alertesWhatsappPourEnvoi,
  categoriesAffichees,
  heuresCalmesAffichees,
  toutesLesCategories,
  verifierHeuresCalmes,
} from '../lib/preferencesWhatsapp';

/** Valeur de l'option « autre numéro » dans la liste des numéros WhatsApp. */
const AUTRE_NUMERO = '__autre__';

/** Les horodatages de l'API sont en UTC: un ISO sans fuseau est lu comme tel. */
const parseIso = (value) => {
  if (!value) return NaN;
  const text = String(value);
  return Date.parse(/(Z|[+-]\d{2}:?\d{2})$/.test(text) ? text : `${text}Z`);
};

/** 597 secondes → « 09:57 ». */
const formatCompteARebours = (secondes) => {
  const minutes = Math.floor(secondes / 60);
  const reste = secondes % 60;
  return `${String(minutes).padStart(2, '0')}:${String(reste).padStart(2, '0')}`;
};

/** Heure du brief du matin tant que le serveur n'en a pas rendu une (défaut de l'API). */
const BRIEF_HEURE_DEFAUT = 7;

/** Les 24 heures proposées pour l'envoi du brief: 0 → « 00 h », 7 → « 07 h ». */
const BRIEF_HEURES = Array.from({ length: 24 }, (_, heure) => heure);
const formatHeureBrief = (heure) => `${String(heure).padStart(2, '0')} h`;

export default function ProfilePage() {
  const navigate = useNavigate();
  // Les mêmes garde-fous que la barre latérale: un raccourci qui mène à
  // « Accès refusé » est pire que pas de raccourci du tout, donc rôle ET
  // module sont vérifiés avant d'afficher quoi que ce soit de cliquable.
  const {
    isAdminSociete,
    canManageUsers,
    canAccessLogistics,
    canAccessTransit,
    canAccessStations,
    canAccessFuelControl,
    isClientPortal,
    hasModule,
    canSeeMoney,
  } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState('compte');
  const [baseline, setBaseline] = useState(null);
  const [profileError, setProfileError] = useState('');
  const [saveError, setSaveError] = useState({});
  const [saving, setSaving] = useState({});
  const locks = useRef(new Set());
  const [visibleNumbers, setVisibleNumbers] = useState(1);
  const [codeRevealed, setCodeRevealed] = useState(false);

  const [formData, setFormData] = useState({ full_name: '', phone: '' });
  const [preferences, setPreferences] = useState({
    whatsapp_alerts: {
      enabled: false,
      numbers: ['', '', ''],
      categories: toutesLesCategories(true),
      heures_calmes: { debut: '', fin: '' },
    },
    language: 'fr',
    default_map: 'osm',
    notifications: true,
  });
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '' });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  // Bot WhatsApp: `bot` est l'état lu côté serveur, `linkInfo` le code qui
  // vient d'être généré. Le serveur ne le renvoie qu'une fois: il vit donc
  // uniquement ici et disparaît au rechargement de la page.
  const [bot, setBot] = useState(null);
  const [botLoading, setBotLoading] = useState(true);
  const [botError, setBotError] = useState('');
  const [botPhoneChoice, setBotPhoneChoice] = useState('');
  const [botPhoneCustom, setBotPhoneCustom] = useState('');
  const [botBusy, setBotBusy] = useState(false);
  const [linkInfo, setLinkInfo] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [briefBusy, setBriefBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [maintenant, setMaintenant] = useState(() => Date.now());

  const identityDirty = Boolean(baseline && JSON.stringify(formData) !== JSON.stringify(baseline.identity));
  const preferencesDirty = Boolean(baseline && JSON.stringify(preferences) !== JSON.stringify(baseline.preferences));
  const hasDrafts = identityDirty || preferencesDirty;
  const busy = (key) => locks.current.has(key);
  const begin = (key) => {
    if (locks.current.has(key)) return false;
    locks.current.add(key);
    return true;
  };
  const end = (key) => locks.current.delete(key);

  useEffect(() => {
    if (!hasDrafts) return undefined;
    const preventLoss = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', preventLoss);
    return () => window.removeEventListener('beforeunload', preventLoss);
  }, [hasDrafts]);

  useEffect(() => {
    loadProfile();
  }, []);

  // Un échec du bot ne bloque pas les autres rubriques ; la reprise est locale.
  const loadBot = useCallback(async () => {
    try {
      const data = await whatsappBotAPI.me();
      setBot(current => data ? ({
        ...data,
        ...(locks.current.has('brief') ? { brief: current?.brief } : {}),
        ...(locks.current.has('ai') ? { ai_enabled: current?.ai_enabled } : {}),
      }) : null);
      setBotError('');
    } catch (error) {
      setBotError(messageErreur(error, 'Impossible de lire le statut du bot'));
    } finally { setBotLoading(false); }
  }, []);

  useEffect(() => {
    loadBot();
  }, [loadBot]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await usersAPI.getMe();
      setProfile(data);
      setProfileError('');
      const identity = { full_name: data.user?.full_name || '', phone: data.user?.phone || '' };
      setFormData(identity);
      const storedWhatsApp = data.preferences?.whatsapp_alerts || data.user?.preferences?.whatsapp_alerts;
      const storedNumbers = Array.isArray(storedWhatsApp?.numbers)
        ? storedWhatsApp.numbers
        : (typeof storedWhatsApp === 'string' ? storedWhatsApp.split(/[,;]/).map((item) => item.trim()).filter(Boolean) : []);
      const alertesActives = storedWhatsApp?.enabled ?? storedNumbers.length > 0;
      const normalized = {
        whatsapp_alerts: {
          enabled: alertesActives,
          numbers: [...storedNumbers.slice(0, 3), '', '', ''].slice(0, 3),
          // Les choix d'alertes: ceux enregistrés, sinon ce que le compte reçoit
          // réellement (tout, s'il était déjà abonné) ou les défauts du rôle.
          categories: categoriesAffichees({
            stockees: storedWhatsApp?.categories,
            actif: alertesActives,
            parDefaut: data.whatsapp_alert_defaults?.categories,
          }),
          heures_calmes: heuresCalmesAffichees(storedWhatsApp?.heures_calmes),
        },
        language: data.preferences?.language || data.user?.preferences?.language || 'fr',
        default_map: data.preferences?.default_map || data.user?.preferences?.default_map || 'osm',
        notifications: data.preferences?.notifications ?? data.user?.preferences?.notifications ?? true,
      };
      setPreferences(normalized);
      setBaseline({ identity, preferences: normalized });
      setVisibleNumbers(Math.max(1, ...storedNumbers.slice(0, 3).map((number, index) => number ? index + 1 : 1)));
    } catch (e) {
      setProfileError(messageErreur(e, 'Impossible de charger votre profil'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (event) => {
    event.preventDefault();
    if (!begin('identity')) return;
    const submitted = { ...formData };
    setSaving(current => ({ ...current, identity: true }));
    setSaveError(current => ({ ...current, identity: '' }));
    try {
      const saved = await usersAPI.updateMy(submitted);
      const identity = { full_name: saved?.full_name ?? submitted.full_name, phone: saved?.phone ?? submitted.phone };
      setProfile(current => ({ ...current, user: { ...current.user, ...identity } }));
      setFormData(identity);
      setBaseline(current => ({ ...current, identity }));
      toast.success('Profil mis à jour');
    } catch (error) {
      const message = messageErreur(error, 'Votre profil n’a pas pu être mis à jour');
      setSaveError(current => ({ ...current, identity: message }));
      toast.error(message);
    } finally { end('identity'); setSaving(current => ({ ...current, identity: false })); }
  };

  const closePassword = (open) => {
    if (busy('password')) return;
    setShowPasswordModal(open);
    if (!open) setPasswordForm({ current_password: '', new_password: '' });
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    if (!begin('password')) return;
    setSaving(current => ({ ...current, password: true }));
    try {
      await usersAPI.changeMyPassword(passwordForm.current_password, passwordForm.new_password);
      setPasswordForm({ current_password: '', new_password: '' });
      setShowPasswordModal(false);
      toast.success('Mot de passe modifié');
    } catch (error) {
      toast.error(messageErreur(error, 'Le mot de passe n’a pas pu être modifié'));
    } finally { end('password'); setSaving(current => ({ ...current, password: false })); }
  };

  const handleRegenerateCode = async () => {
    if (busy('code') || !window.confirm('Régénérer votre code d’accès ? L’ancien code ne permettra plus de vous connecter.')) return;
    if (!begin('code')) return;
    setSaving(current => ({ ...current, code: true }));
    setCodeRevealed(false);
    try {
      const saved = await usersAPI.regenerateMyAccessCode();
      setProfile(current => ({ ...current, user: { ...current.user, access_code: saved.access_code } }));
      toast.success('Code d’accès régénéré');
    } catch (error) {
      toast.error(messageErreur(error, 'Le code d’accès n’a pas pu être régénéré'));
    } finally { end('code'); setSaving(current => ({ ...current, code: false })); }
  };

  const handleSavePreferences = async (event) => {
    event.preventDefault();
    const problem = verifierHeuresCalmes(preferences.whatsapp_alerts.heures_calmes);
    if (problem) {
      setSaveError(current => ({ ...current, preferences: problem }));
      toast.error(problem);
      return;
    }
    if (!begin('preferences')) return;
    const submitted = preferences;
    setSaving(current => ({ ...current, preferences: true }));
    setSaveError(current => ({ ...current, preferences: '' }));
    try {
      await usersAPI.updateMyPreferences({ ...submitted, whatsapp_alerts: alertesWhatsappPourEnvoi(submitted.whatsapp_alerts) });
      setBaseline(current => ({ ...current, preferences: submitted }));
      toast.success('Préférences enregistrées');
    } catch (error) {
      const message = messageErreur(error, 'Les préférences n’ont pas pu être enregistrées');
      setSaveError(current => ({ ...current, preferences: message }));
      toast.error(message);
    } finally { end('preferences'); setSaving(current => ({ ...current, preferences: false })); }
  };

  const handleGenerateLinkCode = async () => {
    if (busy('bot')) return;
    if (!phoneToLink) {
      toast.error('Saisissez le numéro WhatsApp à lier');
      return;
    }
    if (!begin('bot')) return;
    setBotBusy(true);
    try {
      const result = await whatsappBotAPI.requestLinkCode(phoneToLink);
      setLinkInfo(result);
      setMaintenant(Date.now());
      setCodeCopied(false);
      await loadBot();
    } catch (e) {
      toast.error(messageErreur(e, 'Impossible de générer le code de liaison'));
    } finally {
      end('bot');
      setBotBusy(false);
    }
  };

  const handleCopyLinkCode = async () => {
    try {
      await navigator.clipboard.writeText(`LIER ${linkInfo.code}`);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch (e) {
      toast.error('Copie impossible : envoyez LIER suivi du code affiché');
    }
  };

  const handleUnlinkBot = async () => {
    if (!window.confirm('Délier ce numéro ? Le bot ne répondra plus à ce numéro tant que vous ne l’aurez pas relié de nouveau.')) return;
    if (!begin('bot')) return;
    setBotBusy(true);
    try {
      await whatsappBotAPI.unlink();
      setLinkInfo(null);
      await loadBot();
      toast.success('Numéro WhatsApp délié');
    } catch (e) {
      toast.error(messageErreur(e, 'Impossible de délier le numéro'));
    } finally {
      end('bot');
      setBotBusy(false);
    }
  };

  // Brief du matin: l'écran bouge tout de suite (interrupteur et heure), puis
  // le serveur confirme. En cas d'échec on revient à l'état d'avant et on dit
  // pourquoi, plutôt que de laisser croire que le réglage est pris.
  const handleBriefChange = async (changes) => {
    if (!begin('brief')) return;
    const avant = {
      enabled: Boolean(bot?.brief?.enabled),
      hour: bot?.brief?.hour ?? BRIEF_HEURE_DEFAUT,
    };
    const apres = { ...avant, ...changes };
    setBot((current) => (current ? { ...current, brief: apres } : current));
    setBriefBusy(true);
    try {
      const saved = await whatsappBotAPI.setBrief(apres.enabled, apres.hour);
      setBot((current) => (current ? {
        ...current,
        brief: { enabled: Boolean(saved?.enabled), hour: saved?.hour ?? apres.hour },
      } : current));
    } catch (e) {
      setBot((current) => (current ? { ...current, brief: avant } : current));
      toast.error(messageErreur(e, 'Impossible d’enregistrer le brief du matin'));
    } finally {
      end('brief');
      setBriefBusy(false);
    }
  };

  const handleAiToggle = async (enabled) => {
    if (busy('ai')) return;
    if (enabled && !window.confirm(
      'Activer l’assistant IA ? Les messages libres envoyés au bot seront transmis au fournisseur d’IA '
      + '(Anthropic) pour être compris, avec les données d’exploitation utiles. Le prix d’achat confidentiel '
      + 'n’est jamais transmis.',
    )) return;
    if (!begin('ai')) return;
    const avant = bot?.ai_enabled;
    setBot((current) => (current ? { ...current, ai_enabled: enabled } : current));
    setAiBusy(true);
    try {
      await whatsappBotAPI.setAiEnabled(enabled);
      toast.success(enabled ? 'Assistant IA activé pour la société' : 'Assistant IA désactivé');
    } catch (e) {
      setBot((current) => (current ? { ...current, ai_enabled: avant } : current));
      toast.error(messageErreur(e, 'Impossible de modifier l’assistant IA'));
    } finally {
      end('ai');
      setAiBusy(false);
    }
  };

  const handleDemoBotToggle = async () => {
    const next = !bot?.demo_bot_enabled;
    if (!window.confirm(next
      ? 'Activer le bot WhatsApp pour cette présentation ?'
      : 'Désactiver le bot WhatsApp après la présentation ?')) return;
    if (!begin('bot')) return;
    setBotBusy(true);
    try {
      await whatsappBotAPI.setDemoEnabled(next);
      await loadBot();
      toast.success(next ? 'Bot WhatsApp de démonstration activé' : 'Bot WhatsApp de démonstration désactivé');
    } catch (e) {
      toast.error(messageErreur(e, 'Impossible de modifier le bot de démonstration'));
    } finally {
      end('bot');
      setBotBusy(false);
    }
  };

  const handleLogout = () => {
    if (hasDrafts && !window.confirm('Quitter sans enregistrer vos modifications ?')) return;
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  /**
   * Les destinations que ce compte peut réellement ouvrir.
   *
   * Chaque garde reprend mot pour mot celle de `Layout.js` (rôle + module) au
   * lieu de deviner: les accès rapides puisent dans cette liste, donc ils ne
   * peuvent pas proposer une page interdite.
   */
  const destinations = useMemo(() => {
    if (isClientPortal) return [];
    const items = [];
    if (canManageUsers) {
      items.push({
        to: '/users',
        icon: Users,
        titre: 'Utilisateurs et limites',
        detail: 'Gérer les accès de votre organisation',
      });
    }
    // Les alertes sont ouvertes à tous les métiers que la barre latérale sert,
    // mais la page reste conditionnée au module côté route.
    if (
      (canAccessLogistics || canAccessStations || canAccessFuelControl)
      && hasModule('alerts')
    ) {
      items.push({
        to: '/alerts',
        icon: Bell,
        titre: 'Alertes et actions',
        detail: 'Configurer vos alertes',
      });
    }
    if ((isAdminSociete || canAccessTransit) && hasModule('clients')) {
      items.push({ to: '/clients', icon: Handshake, titre: 'Clients', detail: 'Donneurs d’ordre et contrats' });
    }
    if ((isAdminSociete || canAccessTransit) && hasModule('orders')) {
      items.push({ to: '/orders', icon: ShoppingCart, titre: 'Commandes', detail: 'Demandes en cours et à répartir' });
    }
    if (isAdminSociete && hasModule('operations')) {
      items.push({ to: '/operations', icon: Truck, titre: 'Convois', detail: 'Départs groupés et escortes' });
    }
    if ((isAdminSociete || canAccessTransit) && hasModule('trips')) {
      items.push({ to: '/trips', icon: Map, titre: 'Trajets', detail: 'Missions en route et terminées' });
    }
    if ((isAdminSociete || canAccessTransit || canAccessLogistics) && hasModule('trucks')) {
      items.push({ to: '/trucks', icon: Truck, titre: 'Camions', detail: 'Le parc et ses matricules' });
    }
    if ((isAdminSociete || canAccessTransit || canAccessLogistics) && hasModule('drivers')) {
      items.push({ to: '/drivers', icon: IdCard, titre: 'Chauffeurs', detail: 'Conducteurs et permis' });
    }
    return items;
  }, [
    isAdminSociete,
    isClientPortal,
    canManageUsers,
    canAccessLogistics,
    canAccessTransit,
    canAccessStations,
    canAccessFuelControl,
    hasModule,
  ]);

  // Deux raccourcis au maximum pour garder le contexte compact.
  const raccourcis = isAdminSociete
    ? destinations.filter(item => item.to === '/users' || item.to === '/alerts')
    : destinations.slice(0, 2);

  // Numéros proposés pour la liaison: ceux des alertes WhatsApp, plus un champ
  // libre pour un numéro qui n'y figure pas.
  const alertNumbers = [
    ...new Set((baseline?.preferences.whatsapp_alerts.numbers || []).map((number) => number.trim()).filter(Boolean)),
  ];
  const phoneChoice =
    botPhoneChoice === AUTRE_NUMERO || alertNumbers.includes(botPhoneChoice)
      ? botPhoneChoice
      : (alertNumbers[0] || AUTRE_NUMERO);
  const phoneToLink = (phoneChoice === AUTRE_NUMERO ? botPhoneCustom : phoneChoice).trim();

  // Compte à rebours du code affiché: calculé depuis l'heure d'expiration
  // donnée par l'API plutôt que décrémenté, pour ne pas dériver quand l'onglet
  // dort.
  const expiresMs = linkInfo ? parseIso(linkInfo.expires_at) : NaN;
  const secondesRestantes = Number.isFinite(expiresMs)
    ? Math.max(0, Math.ceil((expiresMs - maintenant) / 1000))
    : 0;
  const codeExpire = Boolean(linkInfo) && secondesRestantes === 0;
  const codeActif = Boolean(linkInfo) && !codeExpire;

  // L'horloge ne tourne que tant qu'un code est valable.
  useEffect(() => {
    if (!codeActif) return undefined;
    const timer = setInterval(() => setMaintenant(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [codeActif]);

  // La liaison se termine depuis le téléphone, pas depuis cette page: tant que
  // le code est valable, on relit l'état pour basculer sur « Lié » seul.
  useEffect(() => {
    if (!bot?.pending) return undefined;
    const timer = setInterval(loadBot, 5000);
    return () => clearInterval(timer);
  }, [bot?.pending, loadBot]);

  // Le code vient d'expirer: le serveur oublie la liaison en attente.
  useEffect(() => {
    if (codeExpire) loadBot();
  }, [codeExpire, loadBot]);

  useEffect(() => {
    if (bot?.linked && linkInfo) {
      setLinkInfo(null);
      toast.success('Numéro WhatsApp lié au bot');
    }
  }, [bot?.linked, linkInfo]);

  if (loading) return <div className="profil-loading" role="status"><span />Chargement de votre espace…</div>;
  if (profileError) return <div className="profil-loading" role="alert"><p>{profileError}</p><button type="button" className="btn-secondary" onClick={loadProfile}>Réessayer</button></div>;

  const { user, organization, driver, active_trip, assigned_truck, license, platform_stats } = profile || {};
  const role = user?.role;

  // Un seul état à la fois, dans l'ordre où il se lit: ce qui bloque d'abord
  // (démo, accès non accordé), puis où en est la liaison.
  const pendingExpires = bot?.pending ? parseIso(bot.pending.expires_at) : NaN;
  let botStatut = null;
  if (bot) {
    if (bot.demo && !bot.demo_bot_enabled) {
      botStatut = {
        variant: 'muted',
        label: 'Indisponible',
        detail: 'Le bot WhatsApp n’est pas disponible sur une organisation de démonstration.',
      };
    } else if (!bot.granted) {
      botStatut = {
        variant: 'muted',
        label: 'Accès non accordé',
        detail: 'L’administrateur de votre société doit d’abord vous ouvrir l’accès au bot (page Utilisateurs).',
      };
    } else if (bot.linked) {
      botStatut = {
        variant: 'success',
        label: 'Lié',
        detail: `Numéro ${bot.phone_masked || '—'}${
          bot.linked_at ? ` · depuis le ${formatDate(new Date(parseIso(bot.linked_at)))}` : ''
        }`,
      };
    } else if (bot.pending) {
      botStatut = {
        variant: 'warning',
        label: 'En attente',
        detail: `Code généré pour ${bot.pending.phone_masked || '—'}${
          Number.isFinite(pendingExpires)
            ? `, valable jusqu’à ${formatHeure(new Date(pendingExpires))}`
            : ''
        }.`,
      };
    } else {
      botStatut = {
        variant: 'muted',
        label: 'Non lié',
        detail: 'Aucun numéro n’est encore lié à votre compte.',
      };
    }
  }
  const peutLier = Boolean(bot?.granted && (!bot.demo || bot.demo_bot_enabled) && !bot.linked);
  // Le brief part vers le numéro lié: sans liaison il n'y a rien à régler.
  const peutRegler = Boolean(bot?.granted && (!bot.demo || bot.demo_bot_enabled) && bot.linked);
  const brief = {
    enabled: Boolean(bot?.brief?.enabled),
    hour: bot?.brief?.hour ?? BRIEF_HEURE_DEFAUT,
  };

  // Le serveur décide de la méthode PERSONNEL. Pas de repli vers un rôle historique.
  const security = profile?.security || (role !== 'PERSONNEL' ? {
    login_method: SUB_ACCOUNT_ROLES.has(role) ? 'code' : 'email',
    can_change_password: !SUB_ACCOUNT_ROLES.has(role),
    can_regenerate_code: SUB_ACCOUNT_ROLES.has(role),
  } : {});
  const sections = [
    { id: 'compte', label: 'Mon compte', icon: User, dirty: identityDirty },
    { id: 'preferences', label: 'Alertes et préférences', icon: Bell, dirty: preferencesDirty },
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
    { id: 'securite', label: 'Sécurité', icon: Shield },
    { id: 'organisation', label: role === 'SUPER_ADMIN' ? 'Plateforme' : 'Organisation', icon: Building2 },
  ];
  const titles = {
    compte: ['Vos informations personnelles', 'Vos coordonnées pour travailler ensemble.'],
    preferences: ['Le bon signal, au bon moment', 'Choisissez les alertes utiles et vos habitudes d’affichage.'],
    whatsapp: ['Votre lien avec le terrain', 'Dialoguez avec le bot depuis votre numéro personnel.'],
    securite: ['Gardez la main sur votre accès', 'Les réglages adaptés à votre méthode de connexion.'],
    organisation: [role === 'SUPER_ADMIN' ? 'Votre plateforme' : 'Votre cadre de travail', 'Les informations et réglages disponibles pour votre compte.'],
  };
  const selectSection = (id) => { setSection(id); setCodeRevealed(false); };

  return (
    <div className="profil-page mx-auto w-full max-w-page px-4 md:px-6 lg:px-8">
      <PageHero illustration="administration" eyebrow="VOTRE ESPACE" title="Mon profil" />
      <div className="profil-layout">
        <ProfileRail user={user} role={roleLabel(role) || 'Compte'} sections={sections} current={section} onSelect={selectSection} onLogout={handleLogout} />
        <div className="profil-content">
          <div className="profil-section-title"><span className="profil-eyebrow">{sections.find(item => item.id === section)?.label}</span><h2>{titles[section][0]}</h2><p>{titles[section][1]}</p></div>
          <div id="profil-compte" hidden={section !== 'compte'} className="profil-view" role="region" aria-label="Mon compte">
            <div className="profil-overview">
              <form onSubmit={handleUpdateProfile}>
                <ProfileSection title="Identité et coordonnées" icon={User}>
                  <fieldset disabled={saving.identity} className="profil-fields">
                    <div><label htmlFor="profil-nom" className="label-base">Nom complet</label><input id="profil-nom" autoComplete="name" maxLength={255} value={formData.full_name} onChange={event => setFormData({ ...formData, full_name: event.target.value })} className="input-base" /></div>
                    <div><label htmlFor="profil-tel" className="label-base">Téléphone</label><input id="profil-tel" type="tel" autoComplete="tel" maxLength={50} value={formData.phone} onChange={event => setFormData({ ...formData, phone: event.target.value })} className="input-base" /></div>
                  </fieldset>
                  <div className="profil-readonly-list">
                    <ChampLecture icon={Mail} label="Adresse e-mail">{user?.email || 'Non renseignée'}</ChampLecture>
                    {profile?.entitlements?.profile?.name && <ChampLecture icon={Shield} label="Profil d’accès">{profile.entitlements.profile.name}</ChampLecture>}
                  </div>
                  <p className="profil-note">Les accès et les informations de société sont gérés par votre administrateur.</p>
                </ProfileSection>
                {saveError.identity && <p role="alert" className="profil-error">{saveError.identity}</p>}
                {identityDirty && <ProfileSaveBar busy={saving.identity} onReset={() => { setFormData(baseline.identity); setSaveError(current => ({ ...current, identity: '' })); }} />}
              </form>
              <aside className="profil-context" aria-label="Contexte du compte">
                {organization && <div className="profil-company"><Building2 size={22} aria-hidden="true" /><span className="profil-eyebrow">Votre organisation</span><h3>{organization.name || 'Non renseignée'}</h3><p>{organization.address || 'Adresse non renseignée'}</p><button type="button" className="profil-text-button" onClick={() => selectSection('organisation')}>Voir les informations<ChevronRight size={16} aria-hidden="true" /></button></div>}
                {role === 'SUPER_ADMIN' && <div className="profil-company"><Shield size={22} aria-hidden="true" /><span className="profil-eyebrow">Votre périmètre</span><h3>La plateforme MyCarbu</h3><p>Organisations, comptes et licences de la plateforme.</p><button type="button" className="profil-text-button" onClick={() => selectSection('organisation')}>Vue plateforme<ChevronRight size={16} aria-hidden="true" /></button></div>}
                <div className="profil-context-details">
                  {isAdminSociete ? <><h3>Votre licence</h3><dl><div><dt>Offre</dt><dd>{libelle('licensePlan', license?.plan_type || license?.plan, '—')}</dd></div><div><dt>Capacité</dt><dd>{license?.max_users != null ? `${license.max_users} comptes` : 'Non renseignée'}</dd></div><div><dt>Valable jusqu’au</dt><dd>{license?.expires_at ? formatDate(license.expires_at) : 'Non renseigné'}</dd></div></dl></> : <><h3>Vos repères</h3><dl>{profile?.entitlements?.profile?.name && <div><dt>Profil attribué</dt><dd>{profile.entitlements.profile.name}</dd></div>}<div><dt>Connexion</dt><dd>{security.login_method === 'code' ? 'Code d’accès' : security.login_method === 'email' ? 'E-mail' : 'À confirmer'}</dd></div></dl></>}
                  {raccourcis.length > 0 && <div className="profil-quicklinks">{raccourcis.map(item => <LigneAcces key={item.to} {...item} onClick={() => { if (!hasDrafts || window.confirm('Quitter sans enregistrer vos modifications ?')) navigate(item.to); }} />)}</div>}
                </div>
              </aside>
            </div>
          </div>
          <div id="profil-preferences" hidden={section !== 'preferences'} className="profil-view" role="region" aria-label="Alertes et préférences">
            <form onSubmit={handleSavePreferences}>
              <ProfileSection title="Vos alertes et votre affichage" icon={Bell}>
                <fieldset disabled={saving.preferences} className="space-y-5">
          <div className="profil-preferences-alerts space-y-4">
            <div className="flex items-start gap-3">
              <Switch
                id="whatsapp-alertes"
                checked={preferences.whatsapp_alerts.enabled}
                onCheckedChange={(checked) => setPreferences({
                  ...preferences,
                  whatsapp_alerts: { ...preferences.whatsapp_alerts, enabled: checked },
                })}
                className="mt-0.5"
              />
              <div className="min-w-0">
                <div className="flex items-center gap-1">
                  <label htmlFor="whatsapp-alertes" className="cursor-pointer text-sm font-semibold text-foreground">
                    Recevoir les alertes opérationnelles par WhatsApp
                  </label>
                  <Aide titre="Alertes WhatsApp">
                    Jusqu’à trois numéros internationaux peuvent recevoir les alertes de votre organisation. Choisissez ci-dessous celles qui vous concernent.
                  </Aide>
                </div>
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              {preferences.whatsapp_alerts.numbers.slice(0, visibleNumbers).map((number, index) => (
                <div key={index}>
                  <label htmlFor={`whatsapp-${index}`} className="label-base">
                    Numéro WhatsApp {index + 1}
                  </label>
                  <input
                    id={`whatsapp-${index}`}
                    type="tel"
                    inputMode="tel"
                    className="input-base"
                    placeholder="22376123456"
                    value={number}
                    disabled={!preferences.whatsapp_alerts.enabled}
                    onChange={(e) => {
                      const numbers = [...preferences.whatsapp_alerts.numbers];
                      numbers[index] = e.target.value;
                      setPreferences({ ...preferences, whatsapp_alerts: { ...preferences.whatsapp_alerts, numbers } });
                    }}
                  />
                </div>
              ))}
              {visibleNumbers < 3 && <button type="button" className="profil-text-button" disabled={!preferences.whatsapp_alerts.enabled} onClick={() => setVisibleNumbers(current => current + 1)}><Plus size={16} aria-hidden="true" />Ajouter un numéro</button>}
            </div>
            <div className="border-t border-border/70 pt-3">
              <ChoixAlertesWhatsapp
                categories={preferences.whatsapp_alerts.categories}
                heuresCalmes={preferences.whatsapp_alerts.heures_calmes}
                disabled={!preferences.whatsapp_alerts.enabled}
                onCategorie={(code, coche) => setPreferences({
                  ...preferences,
                  whatsapp_alerts: {
                    ...preferences.whatsapp_alerts,
                    categories: { ...preferences.whatsapp_alerts.categories, [code]: coche },
                  },
                })}
                onHeures={(heures) => setPreferences({
                  ...preferences,
                  whatsapp_alerts: { ...preferences.whatsapp_alerts, heures_calmes: heures },
                })}
              />
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label htmlFor="pref-langue" className="label-base">Langue</label>
              <select
                id="pref-langue"
                className="input-base"
                value={preferences.language}
                onChange={(e) => setPreferences({ ...preferences, language: e.target.value })}
              >
                <option value="fr">Français</option>
                <option value="en">Anglais</option>
              </select>
            </div>
            <div>
              <label htmlFor="pref-carte" className="label-base">Style de carte</label>
              <select
                id="pref-carte"
                className="input-base"
                value={preferences.default_map}
                onChange={(e) => setPreferences({ ...preferences, default_map: e.target.value })}
              >
                <option value="osm">Carte standard</option>
                <option value="satellite">Satellite</option>
              </select>
            </div>
          </div>


                </fieldset>
              </ProfileSection>
              {saveError.preferences && <p role="alert" className="profil-error">{saveError.preferences}</p>}
              {preferencesDirty && <ProfileSaveBar busy={saving.preferences} onReset={() => { setPreferences(baseline.preferences); setSaveError(current => ({ ...current, preferences: '' })); }} />}
            </form>
          </div>
          <div id="profil-whatsapp" hidden={section !== 'whatsapp'} className="profil-view" role="region" aria-label="WhatsApp">
      {bot?.eligible && botStatut ? (
        <ProfileSection
          icon={MessageCircle}
          title="Bot WhatsApp"
          subtitle="Interrogez votre flotte et vos stocks depuis WhatsApp"
          contentClassName="space-y-4"
        >
          {!bot.bot_enabled ? (
            <p className="flex items-center gap-1 rounded-xl bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
              Le bot est désactivé sur la plateforme pour le moment.
              <Aide titre="Bot désactivé">Il répondra une fois activé.</Aide>
            </p>
          ) : null}

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <Badge variant={botStatut.variant}>{botStatut.label}</Badge>
            <span className="text-sm text-muted-foreground">{botStatut.detail}</span>
            {bot.demo && bot.can_toggle_demo ? (
              <button
                type="button"
                onClick={handleDemoBotToggle}
                disabled={botBusy}
                className={bot.demo_bot_enabled ? 'btn-secondary ml-auto px-4' : 'btn-primary ml-auto px-4'}
              >
                {bot.demo_bot_enabled ? 'Désactiver le bot' : 'Activer pour la présentation'}
              </button>
            ) : null}
          </div>

          {peutRegler ? (
            <div className="profil-service-setting space-y-3">
              <div className="flex items-start gap-3">
                <Switch
                  id="bot-brief"
                  checked={brief.enabled}
                  onCheckedChange={(checked) => handleBriefChange({ enabled: checked })}
                  disabled={briefBusy}
                  className="mt-0.5"
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-1">
                    <label htmlFor="bot-brief" className="cursor-pointer text-sm font-semibold text-foreground">
                      Brief du matin
                    </label>
                    <Aide titre="Brief du matin">
                      Chaque matin, un résumé (douane, chargement, commandes, pannes, chiffres clés) sur WhatsApp.
                      Les montants ne s’affichent que pour l’administrateur.
                    </Aide>
                  </div>
                </div>
              </div>
              <div className="md:max-w-xs">
                <label htmlFor="bot-brief-heure" className="label-base">Heure d’envoi</label>
                <select
                  id="bot-brief-heure"
                  className="input-base"
                  value={brief.hour}
                  disabled={briefBusy || !brief.enabled}
                  onChange={(e) => handleBriefChange({ hour: Number(e.target.value) })}
                >
                  {BRIEF_HEURES.map((heure) => (
                    <option key={heure} value={heure}>{formatHeureBrief(heure)}</option>
                  ))}
                </select>
              </div>
            </div>
          ) : null}

          {bot.can_toggle_ai ? (
            <div className="profil-service-setting space-y-2">
              <div className="flex items-start gap-3">
                <Switch
                  id="bot-ia"
                  checked={Boolean(bot.ai_enabled)}
                  onCheckedChange={handleAiToggle}
                  disabled={aiBusy}
                  className="mt-0.5"
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-1">
                    <label htmlFor="bot-ia" className="cursor-pointer text-sm font-semibold text-foreground">
                      Assistant IA
                    </label>
                    <Aide titre="Assistant IA">
                      Le bot comprend les messages écrits librement (« le camion AB-123-CD a crevé à Kayes »,
                      « qu’est-ce qui ne va pas aujourd’hui ? ») : il ouvre la bonne rubrique, prépare la saisie
                      ou résume plusieurs rubriques. Rien n’est enregistré sans votre « 1. Confirmer ».
                      L’assistant aide aussi à importer vos camions et vos chauffeurs depuis un fichier Excel :
                      il ne reçoit que les titres de colonnes et quelques lignes d’exemple, sans noms,
                      téléphones ni e-mails, et rien n’est importé avant votre validation.
                      Le prix d’achat confidentiel n’est jamais transmis.
                    </Aide>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Pour toute la société.
                  </p>
                  {bot.ai_enabled && !bot.ai_available ? (
                    <p className="mt-1 text-xs text-muted-foreground">
                      L’assistant n’est pas encore configuré sur la plateforme : le bot répond comme avant.
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}

          {bot.linked ? (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleUnlinkBot}
                disabled={botBusy}
                className="btn-secondary px-5"
              >
                <Unlink className="h-4 w-4" aria-hidden="true" />
                Délier
              </button>
            </div>
          ) : null}

          {peutLier ? (
            <div className="profil-service-setting space-y-3">
              <div className="grid gap-3 md:grid-cols-2">
                {alertNumbers.length > 0 ? (
                  <div>
                    <label htmlFor="bot-numero" className="label-base">Numéro à lier</label>
                    <select
                      id="bot-numero"
                      className="input-base"
                      value={phoneChoice}
                      onChange={(e) => setBotPhoneChoice(e.target.value)}
                    >
                      {alertNumbers.map((number) => (
                        <option key={number} value={number}>{number}</option>
                      ))}
                      <option value={AUTRE_NUMERO}>Autre numéro…</option>
                    </select>
                  </div>
                ) : null}
                {phoneChoice === AUTRE_NUMERO ? (
                  <div>
                    <label htmlFor="bot-numero-autre" className="label-base">
                      {alertNumbers.length > 0 ? 'Autre numéro WhatsApp' : 'Numéro à lier'}
                    </label>
                    <input
                      id="bot-numero-autre"
                      type="tel"
                      inputMode="tel"
                      className="input-base"
                      placeholder="22376123456"
                      value={botPhoneCustom}
                      onChange={(e) => setBotPhoneCustom(e.target.value)}
                    />
                  </div>
                ) : null}
              </div>
              <p className="text-xs text-muted-foreground">
                Format international, sans espace (ex. 22376123456). Le code n’est affiché qu’une seule fois.
              </p>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleGenerateLinkCode}
                  disabled={botBusy || !phoneToLink}
                  className="btn-primary px-5"
                >
                  <Link2 className="h-4 w-4" aria-hidden="true" />
                  {botBusy ? 'Génération…' : 'Générer le code'}
                </button>
              </div>
            </div>
          ) : null}

          {peutLier && codeActif ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl readout-panel p-4">
                <div className="min-w-0">
                  <div className="readout-label text-readout-foreground/60">Code de liaison</div>
                  <div
                    className="readout mt-1.5 select-all text-2xl tracking-[0.14em]"
                    aria-label="Code de liaison"
                  >
                    {linkInfo.code}
                  </div>
                </div>
                <div
                  role="timer"
                  aria-live="off"
                  className="flex items-center gap-1.5 font-mono text-sm tabular-nums"
                >
                  <Clock className="h-4 w-4" aria-hidden="true" />
                  <span>Expire dans {formatCompteARebours(secondesRestantes)}</span>
                </div>
              </div>
              <p className="text-sm font-medium text-foreground">
                {linkInfo.instruction
                  || `Envoyez « LIER ${linkInfo.code} » au bot depuis ce numéro WhatsApp${
                    linkInfo.bot_phone ? ` (${linkInfo.bot_phone})` : ''
                  }.`}
              </p>
              <button type="button" onClick={handleCopyLinkCode} className="btn-secondary w-full">
                {codeCopied ? (
                  <Check className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Copy className="h-4 w-4" aria-hidden="true" />
                )}
                {codeCopied ? 'Message copié' : 'Copier le message LIER'}
              </button>
            </div>
          ) : null}

          {peutLier && codeExpire ? (
            <p className="rounded-xl bg-muted/60 px-3 py-2 text-sm text-muted-foreground">
              Le code a expiré. Générez-en un nouveau pour lier votre numéro.
            </p>
          ) : null}
        </ProfileSection>
      ) : null}


            {bot?.eligible && botError && <p className="profil-note" role="status">Le statut n’a pas pu être actualisé. <button type="button" className="profil-text-button" onClick={loadBot}>Réessayer</button></p>}
            {!bot?.eligible && <ProfileSection title="Dialogue WhatsApp" icon={MessageCircle}><p className="profil-note" role={botLoading ? 'status' : undefined}>{botLoading ? 'Chargement du statut…' : botError || (bot ? 'Le dialogue avec le bot n’est pas disponible pour ce compte. Vos préférences d’alertes restent dans la rubrique dédiée.' : 'Le statut du bot n’est pas disponible.')}</p>{!botLoading && !bot && <button type="button" className="btn-secondary" onClick={loadBot}>Réessayer</button>}</ProfileSection>}
          </div>
          <div id="profil-securite" hidden={section !== 'securite'} className="profil-view" role="region" aria-label="Sécurité">
            <ProfileSection title="Votre connexion" icon={Lock}>
              <div className="profil-security-summary"><Shield size={28} aria-hidden="true" /><div><h3>{security.login_method === 'code' ? 'Un code personnel' : security.login_method === 'email' ? 'E-mail et mot de passe' : 'Méthode gérée par votre administrateur'}</h3><p>{security.login_method === 'code' ? 'Votre code vous appartient. Gardez-le confidentiel.' : security.login_method === 'email' ? 'Utilisez un mot de passe unique pour protéger votre compte.' : 'Aucune action de sécurité n’est disponible tant que votre profil n’est pas confirmé.'}</p></div></div>
              {security.can_change_password && <button type="button" className="btn-secondary" onClick={() => closePassword(true)}><Key size={16} aria-hidden="true" />Changer mon mot de passe</button>}
              {security.can_regenerate_code && <div className="profil-code-zone"><div className="profil-code"><span>Votre code d’accès</span><strong aria-label={codeRevealed ? 'Code d’accès révélé' : 'Code d’accès masqué'}>{codeRevealed ? user?.access_code || 'Indisponible' : '••••••'}</strong><button type="button" className="profil-text-button" onClick={() => setCodeRevealed(current => !current)} aria-pressed={codeRevealed}>{codeRevealed ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}{codeRevealed ? 'Masquer' : 'Afficher le code'}</button></div><button type="button" className="btn-secondary" onClick={handleRegenerateCode} disabled={saving.code}><RefreshCw size={16} aria-hidden="true" />{saving.code ? 'Régénération…' : 'Régénérer le code'}</button></div>}
            </ProfileSection>
            <div className="profil-mobile-logout"><button type="button" onClick={handleLogout} className="btn-secondary"><LogOut size={16} aria-hidden="true" />Déconnexion</button></div>
          </div>
          <div id="profil-organisation" hidden={section !== 'organisation'} className="profil-view space-y-4" role="region" aria-label={role === 'SUPER_ADMIN' ? 'Plateforme' : 'Organisation'}>
      {organization && (
        <ProfileSection icon={Building2} title="Organisation" contentClassName="grid gap-3 md:grid-cols-3">
          <ChampLecture icon={Building2} label="Nom">{organization.name || '—'}</ChampLecture>
          <ChampLecture icon={MapPin} label="Adresse">{organization.address || '—'}</ChampLecture>
          <ChampLecture icon={Phone} label="Téléphone">{organization.phone || '—'}</ChampLecture>
        </ProfileSection>
      )}

      {isAdminSociete && canSeeMoney && hasModule('finance_cockpit') && (
        <ProfileSection icon={CalendarDays} title="Exercice comptable">
          <FiscalYearSetting />
        </ProfileSection>
      )}

      {role === 'SUPER_ADMIN' && platform_stats && (
        <ProfileSection icon={Shield} title="Statistiques plateforme" contentClassName="grid grid-cols-2 gap-3 md:grid-cols-4">
          {/* Des volumes, pas des états: ils restent donc en neutre, et la
              couleur d'alerte garde sa valeur de signal ailleurs. */}
          {[
            { label: 'Organisations', valeur: platform_stats.total_organizations },
            { label: 'Utilisateurs', valeur: platform_stats.total_users },
            { label: 'Trajets actifs', valeur: platform_stats.total_active_trips },
            { label: 'Licences actives', valeur: platform_stats.total_licenses },
          ].map((stat) => (
            <div key={stat.label} className="rounded-xl border border-border/70 bg-muted/40 p-4">
              <div className="kpi-value text-foreground">{stat.valeur ?? '—'}</div>
              <div className="readout-label mt-1.5">{stat.label}</div>
            </div>
          ))}
        </ProfileSection>
      )}

      {role === 'ADMIN_SOCIETE' && license && (
        <ProfileSection icon={CreditCard} title="Licence" contentClassName="grid gap-3 md:grid-cols-3">
          {/* Un plan absent reste absent: inventer « Standard » ferait croire
              à un périmètre que la licence ne couvre peut-être pas. */}
          <ChampLecture icon={CreditCard} label="Plan">{libelle('licensePlan', license.plan_type || license.plan, '—')}</ChampLecture>
          <ChampLecture icon={Users} label="Utilisateurs">{license.max_users ?? '—'}</ChampLecture>
          <ChampLecture icon={CalendarDays} label="Expiration">
            {formatDate(license.expires_at)}
          </ChampLecture>
        </ProfileSection>
      )}

      {role === 'CHAUFFEUR' && driver && (
        <ProfileSection icon={Truck} title="Chauffeur" contentClassName="space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <ChampLecture icon={User} label="Nom">{driver.full_name || '—'}</ChampLecture>
            <ChampLecture icon={Phone} label="Téléphone">{driver.phone || '—'}</ChampLecture>
            <ChampLecture icon={IdCard} label="Permis">{driver.license_number || '—'}</ChampLecture>
            {assigned_truck && (
              <ChampLecture icon={Truck} label="Camion">{assigned_truck.matricule}</ChampLecture>
            )}
          </div>
          {active_trip && (
            // Vert parce que le trajet tourne, pas parce que c'est un encadré:
            // la couleur dit l'état de la mission.
            <div className="rounded-xl border border-success/30 bg-success/10 p-4">
              <div className="text-sm font-semibold text-success">Trajet en cours : {libelle('tripStatus', active_trip.status)}</div>
              <div className="mt-1 text-xs text-success">{active_trip.start_location} → {active_trip.end_location}</div>
            </div>
          )}
        </ProfileSection>
      )}


            {!organization && role !== 'SUPER_ADMIN' && <ProfileSection title="Organisation" icon={Building2}><p className="profil-note">Aucune information d’organisation n’est disponible pour ce compte.</p></ProfileSection>}
          </div>
        </div>
      </div>
      <Dialog open={showPasswordModal} onOpenChange={closePassword}>
        <DialogContent className="profil-password-dialog" onEscapeKeyDown={event => { if (saving.password) event.preventDefault(); }} onPointerDownOutside={event => { if (saving.password) event.preventDefault(); }}>
          <DialogTitle>Changer le mot de passe</DialogTitle><DialogDescription>Choisissez un mot de passe unique et robuste.</DialogDescription>
          <form onSubmit={handleChangePassword} className="space-y-4"><fieldset disabled={saving.password} className="space-y-4"><div><label htmlFor="mdp-actuel" className="label-base">Mot de passe actuel</label><input id="mdp-actuel" type="password" autoComplete="current-password" required value={passwordForm.current_password} onChange={event => setPasswordForm({ ...passwordForm, current_password: event.target.value })} className="input-base" /></div><div><label htmlFor="mdp-nouveau" className="label-base">Nouveau mot de passe</label><input id="mdp-nouveau" type="password" autoComplete="new-password" required minLength={6} value={passwordForm.new_password} onChange={event => setPasswordForm({ ...passwordForm, new_password: event.target.value })} className="input-base" /></div></fieldset><div className="flex flex-wrap justify-end gap-2"><button type="button" onClick={() => closePassword(false)} disabled={saving.password} className="btn-secondary">Annuler</button><button type="submit" disabled={saving.password} className="btn-primary">{saving.password ? 'Modification…' : 'Changer'}</button></div></form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
